// File: app/src/hooks/useFinishBy.js
//
// Finish-by deadline (#8): the time today's session has to end by, and the
// coach-side watcher that suggests a trim when the session heads past it.
//
// The deadline is stored per plan and per day in localStorage, so it survives
// a reload mid-session but never leaks into a different session or tomorrow's.
// The trim maths is in utils/finishBy.js.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import localStorageService from "../services/localStorageService";
import useSound from "./useSound";
import { isNativeApp } from "../platform/native";
import { nativeNotify, setNativeAlarm } from "../platform/nativeCoach";
import {
  describeTrim,
  formatClock,
  projectedOverrunSec,
  proposeTrim,
  shouldOfferTrim,
} from "../utils/finishBy";

const STORAGE_KEY = "khronos_finish_by";
const CHANGE_EVENT = "khronos:finish-by";
// Older than this past its time, a stored deadline is a leftover.
const STALE_MS = 6 * 60 * 60 * 1000;
// Recompute the proposal when the overrun moves by a bucket, not every second.
const PROPOSAL_BUCKET_SEC = 30;

const read = (planId) => {
  const stored = localStorageService.get(STORAGE_KEY);
  if (!stored || !Number.isFinite(stored.at)) return null;
  if (planId != null && stored.planId != null && stored.planId !== planId) {
    return null;
  }
  if (Date.now() - stored.at > STALE_MS) return null;
  return stored;
};

const write = (value) => {
  if (value) localStorageService.set(STORAGE_KEY, value);
  else localStorageService.remove(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
};

// Ask once, from the gesture that set a deadline: a trim suggestion that comes
// up while the phone is in a pocket is only useful as a notification.
const requestNotificationPermission = () => {
  if (isNativeApp()) return; // the app asks for it with the workout notification
  try {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") Notification.requestPermission();
  } catch {
    // Unsupported (iOS outside an installed app): the in-app sheet still works.
  }
};

const notify = async (title, body) => {
  // The Android app posts its own: a WebView has no web notifications.
  if (isNativeApp()) {
    await nativeNotify(title, body);
    return;
  }
  try {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    const options = { body, tag: "khronos-finish-by", renotify: true, icon: "/logo192.png" };
    const reg = await navigator.serviceWorker?.getRegistration?.();
    if (reg) await reg.showNotification(title, options);
    else new Notification(title, options);
  } catch {
    // Best effort: the sheet is waiting in the app either way.
  }
};

/**
 * The deadline for the session on screen.
 *
 * @returns {{ deadline: {at: number, alarm: boolean} | null,
 *             setDeadline: (at: number | null, opts?: {alarm?: boolean}) => void }}
 */
export const useFinishByDeadline = (workoutPlan) => {
  // Generated plans often share a root id ("root"), so the label goes in too.
  const planId = workoutPlan ? `${workoutPlan.id ?? ""}|${workoutPlan.label ?? ""}` : null;
  const [deadline, setState] = useState(() => read(planId));

  useEffect(() => {
    const sync = () => setState(read(planId));
    sync();
    window.addEventListener(CHANGE_EVENT, sync);
    return () => window.removeEventListener(CHANGE_EVENT, sync);
  }, [planId]);

  const setDeadline = useCallback(
    (at, { alarm = false } = {}) => {
      if (at == null) {
        write(null);
        return;
      }
      requestNotificationPermission();
      write({ at, alarm: !!alarm, planId });
    },
    [planId],
  );

  return { deadline, setDeadline };
};

/**
 * Watch a running session against its deadline.
 *
 * @param {object} args
 * @param {object} args.workoutPlan
 * @param {number} args.remainingSec   projected seconds left (useWorkoutUiState)
 * @param {boolean} args.atBreak        a rest, the Ready gate or paused
 * @param {boolean} args.active         the session is under way
 * @param {Function} args.getTrimContext  engine position (useWorkoutCoach)
 * @param {Function} args.applySessionEdit
 * @param {Function} [args.showToast]
 */
export const useFinishByMonitor = ({
  workoutPlan,
  remainingSec,
  atBreak,
  active,
  getTrimContext,
  applySessionEdit,
  showToast,
}) => {
  const { deadline, setDeadline } = useFinishByDeadline(workoutPlan);
  const playSound = useSound();
  const [dismissedAtSec, setDismissedAtSec] = useState(null);

  const now = Date.now();
  const overSec =
    deadline && active
      ? projectedOverrunSec({ now, remainingSec, finishBy: deadline.at })
      : null;

  const offering =
    overSec != null &&
    shouldOfferTrim({ overSec, atBreak, dismissedAtSec });
  const bucket = offering
    ? Math.ceil(overSec / PROPOSAL_BUCKET_SEC) * PROPOSAL_BUCKET_SEC
    : null;

  // The engine position only moves when the phase does; reading it inside the
  // memo keyed on the plan and the bucket is enough.
  const trimCtx = offering ? getTrimContext?.() : null;
  const currentIndex = trimCtx?.currentIndex ?? null;
  const proposal = useMemo(() => {
    if (bucket == null || !trimCtx) return null;
    return proposeTrim(workoutPlan, { ...trimCtx, overSec: bucket });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutPlan, bucket, currentIndex]);

  const offer = proposal
    ? {
        overSec,
        deadlineLabel: formatClock(deadline.at),
        projectedLabel: formatClock(now + remainingSec * 1000),
        text: describeTrim(proposal.changes),
        landsOnTime: proposal.landsOnTime,
        proposal,
      }
    : null;

  // The latest offer, for the callbacks and the notification effect: it is
  // rebuilt every tick (the projected time moves), but they only care about
  // what it says when they run.
  const offerRef = useRef(offer);
  offerRef.current = offer;
  const notifyKey = offer ? `${deadline?.at}:${bucket}` : null;

  // Heading over while the app is in the background: say so on the lock screen.
  const notifiedRef = useRef(null);
  useEffect(() => {
    const current = offerRef.current;
    if (!notifyKey || !current || notifiedRef.current === notifyKey) return;
    notifiedRef.current = notifyKey;
    if (document.visibilityState !== "hidden") return;
    notify(
      `Running past ${current.deadlineLabel}`,
      `${current.text} to finish on time. Open Khronos to choose.`,
    );
  }, [notifyKey]);

  // Optional alarm at the deadline itself, once.
  const alarmedRef = useRef(null);
  const alarmDue = !!(deadline?.alarm && active && now >= deadline.at);
  useEffect(() => {
    if (!alarmDue || alarmedRef.current === deadline.at) return;
    alarmedRef.current = deadline.at;
    const label = formatClock(deadline.at);
    playSound("end");
    showToast?.({ title: `It's ${label}`, description: "Your finish-by time." });
    // In the Android app CoachService sounds and posts the alarm itself, on
    // time even while this page is throttled (below).
    if (document.visibilityState === "hidden" && !isNativeApp()) {
      notify(`It's ${label}`, "Your finish-by time.");
    }
  }, [alarmDue, deadline, playSound, showToast]);

  const nativeAlarmAt = deadline?.alarm && active ? deadline.at : null;
  useEffect(() => {
    if (!isNativeApp()) return undefined;
    if (!nativeAlarmAt) return undefined;
    setNativeAlarm(nativeAlarmAt, `It's ${formatClock(nativeAlarmAt)}`, "Your finish-by time.");
    return () => {
      setNativeAlarm(null);
    };
  }, [nativeAlarmAt]);

  const acceptTrim = useCallback(() => {
    const current = offerRef.current;
    if (!current) return;
    applySessionEdit(current.proposal.plan);
    setDismissedAtSec(null);
    showToast?.({ title: "Session trimmed", description: current.text });
  }, [applySessionEdit, showToast]);

  const keepGoing = useCallback(() => {
    const current = offerRef.current;
    if (current) setDismissedAtSec(current.overSec);
  }, []);

  return { deadline, setDeadline, overSec, offer, acceptTrim, keepGoing };
};
